/**
 * Central product limits for Lesson Launchpad (Phase 1 guardrails).
 * Use these constants in UI and save handlers, not ad-hoc magic numbers.
 */

export const MAX_CLASSES_PER_TEACHER = 75;
export const MAX_ANNOUNCEMENTS = 1000;
export const MAX_CONTENT_BOX_TEMPLATES = 60;
export const MAX_ROSTER_STUDENTS_PER_CLASS = 250;
export const MAX_PLAYLIST_DEFINITIONS = 30;
export const MAX_TRACKS_PER_PLAYLIST = 100;
export const MAX_RANDOMIZER_CUSTOM_LIST_ITEMS = 100;
export const MAX_CONTENT_BOX_TAGS = 30;
export const MAX_CONTENT_BOX_TAG_NAME = 20;
export const MAX_AGENDA_STEPS = 30;
export const MAX_AGENDA_SUBSTEPS_PER_STEP = 20;
export const MAX_CAROUSEL_SLIDES = 30;
export const MAX_RICH_TEXT_HTML_BYTES = 64 * 1024;
/** Soft ceiling under Firestore's ~1,048,487 byte string-field limit. */
export const MAX_CONTENT_BOX_HTML_BYTES = 1_000_000;
export const MAX_ANNOUNCEMENT_PLAIN_TEXT = 300;
export const MAX_TEMPLATE_NAME = 40;
export const MAX_PLAYLIST_NAME = 40;
export const MAX_STUDENT_NAME = 50;
export const MAX_DESCRIPTOR_FIELD = 30;
export const MAX_REMINDER_MESSAGE = 100;
export const MAX_COUNTDOWN_HEADER_ALERT = 30;
export const MAX_EVENT_TITLE = 120;
export const MAX_EVENT_NOTES = 2000;
export const MAX_BACKGROUND_ASSET_URL = 2048;
export const MAX_TEMPLATE_PAYLOAD_BYTES = MAX_RICH_TEXT_HTML_BYTES;

export const PRODUCT_LIMIT_MESSAGES = {
  classLimit:
    "You've reached the class limit for this account. Existing classes are still editable.",
  announcementLimit:
    "You've reached the marquee item limit for this account. You can still edit or remove existing items.",
  announcementText:
    "Each marquee item can be up to 300 characters. Please shorten your message.",
  announcementHtml:
    "This marquee item has too much formatting to save safely. Try simplifying the text or removing extra styling.",
  templateLimit:
    "You've reached the template limit for this account. Delete an unused template to save a new one.",
  templateName: `Template names can be up to ${MAX_TEMPLATE_NAME} characters.`,
  templatePayload:
    "This template is too large to save. Try saving a simpler box or splitting it into two templates.",
  rosterImport:
    `That import has more students than this class can support. Please reduce it to ${MAX_ROSTER_STUDENTS_PER_CLASS} students or fewer.`,
  rosterAdd:
    `This class can have up to ${MAX_ROSTER_STUDENTS_PER_CLASS} students. Remove a student before adding another.`,
  rosterGrandfather:
    `This class has more students than we normally support. You can edit or remove students, but can't add more until you're at ${MAX_ROSTER_STUDENTS_PER_CLASS} or fewer.`,
  playlistLimit:
    "You've reached the playlist limit for this account. Delete an unused playlist to add a new one.",
  playlistTracks:
    "Playlists can include up to 100 tracks. Remove a track before adding another.",
  playlistName: `Playlist names can be up to ${MAX_PLAYLIST_NAME} characters.`,
  tagLimit:
    "You've reached the tag limit for this account. Delete an unused tag to create a new one.",
  tagName: `Tag names can be up to ${MAX_CONTENT_BOX_TAG_NAME} characters.`,
  agendaSteps:
    "Agendas work best with 30 steps or fewer. Try combining smaller steps or splitting into two agenda boxes.",
  agendaSubsteps:
    "This step can have up to 20 sub-steps. Try combining items or splitting into another step.",
  carouselSlides:
    "Carousels can show up to 30 slides. Remove a slide before adding another.",
  richTextHtml:
    "This is a little too much content for one box. Try splitting it into two boxes.",
  contentBoxHtml:
    "This content box is too large to save. Remove a large pasted image or split the content into two boxes, then try again.",
  contentBoxBlobImages:
    "An image is still uploading or failed to upload. Use Insert Image from Library, then try saving again.",
  contentBoxInlineImageUpload:
    "That image couldn't be uploaded automatically. Use Insert Image from Library instead.",
  reminderMessage: `Reminder messages can be up to ${MAX_REMINDER_MESSAGE} characters.`,
  countdownHeader: `Countdown messages can be up to ${MAX_COUNTDOWN_HEADER_ALERT} characters.`,
  eventTitle: `Event titles can be up to ${MAX_EVENT_TITLE} characters.`,
  eventNotes: `Event notes can be up to ${MAX_EVENT_NOTES} characters.`,
  grandfatherGeneric: (label, max, current) =>
    `You have ${current} ${label}, which is above our usual limit of ${max}. Existing items still work, but you can't add more until you remove some.`,
};

const textEncoder =
  typeof TextEncoder !== "undefined" ? new TextEncoder() : null;

/**
 * UTF-8 byte length of a string (for HTML/storage guards).
 */
export function getUtf8ByteLength(value) {
  const normalized = value == null ? "" : String(value);
  if (textEncoder) {
    return textEncoder.encode(normalized).byteLength;
  }
  // Fallback for non-browser test environments without TextEncoder
  return unescape(encodeURIComponent(normalized)).length;
}

export function isUnderByteLimit(value, limit) {
  return getUtf8ByteLength(value) <= limit;
}

export function truncateString(value, limit) {
  const normalized = value == null ? "" : String(value);
  if (normalized.length <= limit) {
    return normalized;
  }
  return normalized.slice(0, limit);
}

/**
 * Count-based guard with grandfather support.
 * @param {object} options
 * @param {number} options.currentCount - Count after the proposed operation
 * @param {number} options.max
 * @param {string} options.label - Human label, e.g. "marquee items"
 * @param {boolean} [options.isNewCreate=false] - Whether this operation adds a new record
 * @param {number} [options.previousCount=0] - Count before the operation (for grandfather edits)
 */
export function validateArrayLimit({
  currentCount,
  max,
  label,
  isNewCreate = false,
  previousCount = 0,
}) {
  const safeCurrent = Number.isFinite(currentCount) ? currentCount : 0;
  const safePrevious = Number.isFinite(previousCount) ? previousCount : 0;
  const safeMax = Number.isFinite(max) ? max : 0;

  if (isNewCreate && safePrevious >= safeMax) {
    return {
      ok: false,
      error: PRODUCT_LIMIT_MESSAGES.grandfatherGeneric(label, safeMax, safePrevious),
    };
  }

  if (isNewCreate && safeCurrent > safeMax) {
    return {
      ok: false,
      error: PRODUCT_LIMIT_MESSAGES.grandfatherGeneric(label, safeMax, safePrevious),
    };
  }

  if (!isNewCreate && safeCurrent > safeMax && safeCurrent > safePrevious) {
    return {
      ok: false,
      error: PRODUCT_LIMIT_MESSAGES.grandfatherGeneric(label, safeMax, safePrevious),
    };
  }

  if (safePrevious > safeMax || safeCurrent > safeMax) {
    return {
      ok: true,
      warning: PRODUCT_LIMIT_MESSAGES.grandfatherGeneric(label, safeMax, Math.max(safePrevious, safeCurrent)),
    };
  }

  return { ok: true };
}

/**
 * HTML / rich-text byte guard.
 */
export function validateHtmlByteLimit({ html, maxBytes = MAX_RICH_TEXT_HTML_BYTES, label = "content" }) {
  const normalized = html == null ? "" : String(html);
  if (!normalized) {
    return { ok: true };
  }

  const byteLength = getUtf8ByteLength(normalized);
  if (byteLength <= maxBytes) {
    return { ok: true };
  }

  return {
    ok: false,
    error:
      label === "marquee"
        ? PRODUCT_LIMIT_MESSAGES.announcementHtml
        : PRODUCT_LIMIT_MESSAGES.richTextHtml,
  };
}

export function validatePlainTextLength({
  text,
  max = MAX_ANNOUNCEMENT_PLAIN_TEXT,
  message = PRODUCT_LIMIT_MESSAGES.announcementText,
}) {
  const normalized = text == null ? "" : String(text);
  if (normalized.length <= max) {
    return { ok: true };
  }
  return { ok: false, error: message };
}

export function validateStringLength({
  value,
  max,
  message,
  fieldName = "value",
}) {
  const normalized = value == null ? "" : String(value);
  if (normalized.length <= max) {
    return { ok: true, value: normalized };
  }
  return {
    ok: false,
    error: message || `${fieldName} can be up to ${max} characters.`,
  };
}

export function getSerializedByteLength(payload) {
  try {
    return getUtf8ByteLength(JSON.stringify(payload));
  } catch {
    return Number.MAX_SAFE_INTEGER;
  }
}
